import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { CheckIcon, SparklesIcon, RocketIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { ContactFormPopup } from './ContactFormPopup';
export function SaleServices() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const {
    t
  } = useLanguage();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  return <>
      <section id="services" ref={ref} className="relative w-full overflow-hidden px-4 py-16 sm:py-24" style={{
      backgroundColor: 'var(--bg-primary)'
    }} data-id="element-244" data-mp="1y7zvkdekjdpx">
        <div className="relative z-10 mx-auto max-w-7xl" data-id="element-245" data-mp="1o8heq5tyg823">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} className="mb-12 sm:mb-16 text-center" data-id="element-246" data-mp="1ob3rqkzm7frp">
            <h2 className="mb-4 text-3xl font-bold md:text-4xl lg:text-5xl" style={{
            color: 'var(--text-primary)'
          }} data-id="element-247" data-mp="2npbw6zqwf6sf">
              {t('sale.pricing.title')}{' '}
              <span style={{
              color: 'var(--accent)'
            }} data-id="element-248" data-mp="1v1pkt5bfixv0">
                {t('sale.pricing.titleHighlight')}
              </span>
            </h2>
            <p className="mx-auto max-w-2xl text-base sm:text-lg" style={{
            color: 'var(--text-secondary)'
          }} data-id="element-249" data-mp="2435ufauf6vy6">
              {t('sale.pricing.subtitle')}
            </p>
          </motion.div>

          {/* Pricing Cards */}
          <div className="grid gap-6 lg:grid-cols-2 max-w-5xl mx-auto" data-id="element-250" data-mp="3bempxx9u00x2">
            {/* Free Demo Preview */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6,
            delay: 0.1
          }} data-id="element-251" data-mp="2840mtvsudy8d">
              <div className="h-full rounded-3xl border p-8 flex flex-col" style={{
              borderColor: 'var(--border)',
              backgroundColor: 'var(--bg-secondary)'
            }} data-id="element-252" data-mp="1ucvi4famafr6">
                <div className="flex items-center gap-3 mb-6" data-id="element-253" data-mp="236qlpiw3by4p">
                  <div className="p-3 rounded-xl" style={{
                  backgroundColor: 'var(--bg-tertiary)'
                }} data-id="element-254" data-mp="16o2sf0kl9450">
                    <SparklesIcon className="w-6 h-6" style={{
                    color: 'var(--accent)'
                  }} data-id="element-255" data-mp="135ltu50lriec" />
                  </div>
                  <h3 className="text-xl font-bold" style={{
                  color: 'var(--text-primary)'
                }} data-id="element-256" data-mp="u344vitkcbnn">
                    {t('pricing.demo.title')}
                  </h3>
                </div>

                <div className="mb-6" data-id="element-257" data-mp="fezkmaev3s2r">
                  <div className="inline-block px-4 py-1 rounded-full text-sm font-bold mb-2" style={{
                  backgroundColor: 'var(--accent)',
                  color: '#fff'
                }} data-id="element-258" data-mp="fdy8e2m40zjg">
                    {t('pricing.demo.label')}
                  </div>
                  <p className="text-sm sm:text-base font-medium mt-2" style={{
                  color: 'var(--text-secondary)'
                }} data-id="element-259" data-mp="3sncf3nbsm0p5">
                    {t('pricing.demo.desc')}
                  </p>
                </div>

                <ul className="space-y-4 mb-8 flex-1" data-id="element-260" data-mp="1msbmgnrave6o">
                  {[1, 2, 3].map(i => <li key={i} className="flex items-start gap-3" data-id="element-261" data-mp="1rremftsfau7s">
                      <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center" data-id="element-262" data-mp="1dsx3089jw3vo">
                        <CheckIcon className="w-3 h-3 text-green-500" data-id="element-263" data-mp="1hv1rksl56abg" />
                      </div>
                      <span style={{
                    color: 'var(--text-secondary)'
                  }} data-id="element-264" data-mp="13vsnuse4h8od">
                        {t(`pricing.demo.b${i}`)}
                      </span>
                    </li>)}
                </ul>

                <button onClick={() => setIsPopupOpen(true)} className="block w-full rounded-xl py-4 text-center text-sm sm:text-base font-semibold transition-all hover:scale-[1.02]" style={{
                backgroundColor: 'var(--accent)',
                color: '#FFFFFF'
              }} data-id="element-265" data-mp="337388htxxn70">
                  {t('pricing.demo.cta')}
                </button>
              </div>
            </motion.div>

            {/* Website Subscription */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6,
            delay: 0.2
          }} data-id="element-266" data-mp="1ackgihxmangd">
              <div className="h-full rounded-3xl border p-8 flex flex-col relative overflow-hidden" style={{
              borderColor: 'var(--border)',
              backgroundColor: 'var(--bg-secondary)'
            }} data-id="element-267" data-mp="3h1qk7eyk6n2o">
                <div className="flex items-center gap-3 mb-6 relative z-10" data-id="element-268" data-mp="jkwoaluk9nxb">
                  <div className="p-3 rounded-xl" style={{
                  backgroundColor: 'var(--bg-tertiary)'
                }} data-id="element-269" data-mp="jmpu5yxco449">
                    <RocketIcon className="w-6 h-6" style={{
                    color: 'var(--accent)'
                  }} data-id="element-270" data-mp="tgzzsibayywx" />
                  </div>
                  <h3 className="text-xl font-bold" style={{
                  color: 'var(--text-primary)'
                }} data-id="element-271" data-mp="179xyoemyf958">
                    {t('sale.pricing.launch.title')}
                  </h3>
                </div>

                <div className="mb-6 relative z-10" data-id="element-272" data-mp="31q68f1ds782p">
                  <div className="flex items-baseline gap-2 flex-wrap" data-id="element-273" data-mp="31s0qm7xo4m33">
                    <span className="text-2xl font-bold line-through opacity-50" style={{
                    color: 'var(--text-muted)'
                  }} data-id="element-274" data-mp="2wqbfksag48f0">
                      €25
                    </span>
                    <span className="text-4xl font-bold" style={{
                    color: 'var(--text-primary)'
                  }} data-id="element-275" data-mp="3khgy5f7rtet0">
                      {t('sale.pricing.launch.price')}
                    </span>
                    <span className="text-sm" style={{
                    color: 'var(--text-muted)'
                  }} data-id="element-276" data-mp="35skpph1sxzrv">
                      {t('sale.pricing.launch.period')}
                    </span>
                  </div>
                  <p className="text-sm sm:text-base font-medium mt-2" style={{
                  color: 'var(--text-secondary)'
                }} data-id="element-277" data-mp="36slzdkz3ulqs">
                    {t('sale.pricing.launch.desc')}
                  </p>
                </div>

                <ul className="space-y-4 mb-8 flex-1 relative z-10" data-id="element-278" data-mp="32sljrx09s4i5">
                  {[1, 2, 3, 4].map(i => <li key={i} className="flex items-start gap-3" data-id="element-279" data-mp="3qhmk0wyy89cc">
                      <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center" data-id="element-280" data-mp="3uizr1ads2iku">
                        <CheckIcon className="w-3 h-3 text-green-500" data-id="element-281" data-mp="3gif7ud3yakqt" />
                      </div>
                      <span style={{
                    color: 'var(--text-secondary)'
                  }} data-id="element-282" data-mp="3lggpctlqac06">
                        {t(`pricing.launch.b${i}`)}
                      </span>
                    </li>)}
                </ul>

                <button onClick={() => setIsPopupOpen(true)} className="block w-full rounded-xl py-4 text-center text-sm sm:text-base font-semibold transition-all hover:scale-[1.02] relative z-10" style={{
                backgroundColor: 'var(--accent)',
                color: '#FFFFFF'
              }} data-id="element-283" data-mp="22hivqgrcrse0">
                  {t('pricing.launch.cta')}
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <ContactFormPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} data-id="element-284" data-mp="1slplx550qczx" />
    </>;
}